import {FC, useEffect, useRef, useState} from 'react'
import {Content} from '@prismicio/client'
import {SliceComponentProps} from '@prismicio/react'
import {Container} from 'src/common/Container'
import {PrismicNextImage} from '@prismicio/next'
import {sanitizeHtml} from 'src/common/helpers/sanitizeHtml'
import {AnimatePresence, motion, useInView} from 'framer-motion'
import cn from 'classnames'

/**
 * Props for `AnimatedList`.
 */
export type AnimatedListProps = SliceComponentProps<Content.AnimatedListSlice>

/**
 * Component for "AnimatedList" Slices.
 */
const AnimatedList: FC<AnimatedListProps> = ({slice}) => {
  const sanitizedTitle = sanitizeHtml(slice.primary.title!)
  const containerRef = useRef(null)
  const [activeStyle, setActiveStyle] = useState(0)
  const listItems = slice.primary.elements.length
  const isInView = useInView(containerRef, {amount: 0.5, once: false})

  // Start animation when the component is in view
  useEffect(() => {
    if (!isInView) {
      return
    }

    const interval = setInterval(() => {
      setActiveStyle((prev) => {
        if (prev + 1 < listItems) {
          return prev + 1
        } else {
          return 0
        }
      })
    }, 2000)

    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- not need to add listItems dependency
  }, [setActiveStyle, isInView])

  return (
    <Container
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="relative h-[800px] overflow-visible !py-0"
      containerRef={containerRef}
    >
      <div className="sticky top-0 isolate h-full w-full">
        <PrismicNextImage
          quality={100}
          field={slice.primary.background_image}
          className="absolute left-0 top-0 -z-10 h-full w-full object-cover"
        />
        <div className="mx-auto flex h-full w-full max-w-[1200px] flex-col justify-between px-5 py-10 md:px-10 lg:px-5 lg:py-16 lg:pb-18 xl:px-0">
          <h2
            className="lg:text-tg-h2 text-[24px] leading-[normal] text-moss-white lg:leading-[normal]"
            dangerouslySetInnerHTML={{__html: sanitizedTitle}}
          />
          <div className="flex w-full flex-col">
            <div className="w-fit self-start pb-6 lg:hidden">
              <AnimatePresence mode="sync">
                <div className="relative flex items-center gap-3 rounded-[4px] bg-moss-white/[20%] py-2 pl-2 pr-3 backdrop-blur-[40px] transition-opacity duration-700 md:gap-4 md:rounded-lg md:py-2.5 md:pl-2.5 md:pr-4">
                  <motion.img
                    key={'list-item-' + activeStyle}
                    src={slice.primary.elements[activeStyle].icon.url || ''}
                    className="h-8 w-8 md:h-[42px] md:w-[42px]"
                    initial={{opacity: 0}}
                    animate={{opacity: 1}}
                    exit={{opacity: 0}}
                    transition={{duration: 0.7}}
                  />
                  <motion.span
                    className="text-[13px] font-semibold uppercase leading-[14px] tracking-[.78px] text-moss-white md:text-[18px] md:leading-normal md:tracking-[.54px]"
                    initial={{opacity: 0}}
                    animate={{opacity: 1}}
                    exit={{opacity: 0}}
                    transition={{duration: 0.7}}
                    key={'list-item-text-' + activeStyle}
                  >
                    {slice.primary.elements[activeStyle].side_text}
                  </motion.span>
                  <div
                    className="pointer-events-none absolute inset-0 rounded-md"
                    style={{
                      border: '1.54px solid transparent',
                      background:
                        'linear-gradient(to right, rgba(255,255,255,0.4), rgba(255,255,255,0.15), rgba(255,255,255,0)) border-box',
                      WebkitMask: 'linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)',
                      WebkitMaskComposite: 'xor',
                      maskComposite: 'exclude',
                    }}
                  />
                </div>
              </AnimatePresence>
            </div>
            <ul className="flex flex-col gap-[11px]">
              {slice.primary.elements.map((element, index) => (
                <li className="flex w-full items-center gap-3" key={index}>
                  <h3
                    className={cn(
                      'w-full text-[clamp(32px,5vw,64px)] leading-9 tracking-[-.32px] transition-colors duration-700 lg:w-[70%] lg:max-w-[835px] lg:leading-[100%] lg:tracking-[-.64px]',
                      {
                        'text-moss-white': activeStyle === index,
                        'text-moss-white/[70%]': activeStyle !== index,
                      },
                    )}
                  >
                    {element.text}
                  </h3>
                  <AnimatePresence mode="wait">
                    {activeStyle === index && (
                      <motion.div
                        className="relative flex items-center gap-[16px] rounded-lg border-moss-white bg-moss-white/[20%] py-2.5 pl-2.5 pr-4 backdrop-blur-[40px] transition-opacity duration-700 max-lg:hidden"
                        initial={{opacity: 0}}
                        animate={{opacity: 1, transition: {duration: 0.7, delay: 0.2, ease: 'easeIn'}}}
                        exit={{opacity: 0, transition: {duration: 0.7}}}
                        key={'list-item-' + index}
                      >
                        <PrismicNextImage field={element.icon} className="h-[42px] w-[42px] rounded-[5.33px]" />
                        <span className="text-[18px] font-semibold uppercase leading-normal tracking-[.54px] text-moss-white">
                          {element.side_text}
                        </span>
                        <div
                          className="pointer-events-none absolute inset-0 rounded-md"
                          style={{
                            border: '1.54px solid transparent',
                            background:
                              'linear-gradient(to right, rgba(255,255,255,0.4), rgba(255,255,255,0.15), rgba(255,255,255,0)) border-box',
                            WebkitMask: 'linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)',
                            WebkitMaskComposite: 'xor',
                            maskComposite: 'exclude',
                          }}
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </Container>
  )
}

export default AnimatedList
